import { createMemo, For, Show } from "solid-js";
import { controlePlansAction, PONDERATIONS } from "../domaine/analyses";
import { PLACEMENTS, type Placement } from "../domaine/graphe";
import { etatTemporel } from "../domaine/portefeuille";
import type { DimensionSimilarite, Poids, Similarites } from "../domaine/similarite";
import type { DateIso, EtatTemporel, Portefeuille } from "../domaine/types";
import { formaterScore } from "../format";
import { ReglageApparence } from "./ReglageApparence";

export const LIBELLES_DIMENSION: Record<DimensionSimilarite, string> = {
  leviers: "Familles de leviers",
  cultures: "Cultures",
  bioagresseurs: "Bioagresseur visé",
};

/** Libellés du filtre d'avancement, au pluriel : ils comptent des projets. */
export const LIBELLES_ETAT_FILTRE: Record<EtatTemporel, string> = {
  encours: "en cours",
  termine: "terminés",
  avenir: "à venir",
  inconnu: "date inconnue",
};

const PAIRES_TEMOIN = 20;

/** États présents dans le portefeuille complet : le plus fourni d'abord, « inconnu » en fin. */
export function etatsPresents(
  portefeuille: Portefeuille,
  aujourdhui: DateIso,
): { etat: EtatTemporel; n: number }[] {
  const vus = new Map<EtatTemporel, number>();
  for (const p of portefeuille.projets) {
    const e = etatTemporel(p, aujourdhui);
    vus.set(e, (vus.get(e) ?? 0) + 1);
  }
  return [...vus.entries()]
    .sort((a, b) => {
      if (a[0] === "inconnu") return 1;
      if (b[0] === "inconnu") return -1;
      return b[1] - a[1];
    })
    .map(([etat, n]) => ({ etat, n }));
}

/**
 * Ce qui règle le modèle, pas l'affichage, dans l'ordre où ça intervient : la
 * donnée affichée, les projets qui entrent dans le calcul, puis ce que « se
 * ressembler » veut dire. Les projets exclus étant hors de vue ailleurs, un
 * bandeau le rappelle sur toutes les vues.
 */
export function VueParametres(props: {
  /** Portefeuille complet, avant filtre d'avancement : un état exclu doit rester cochable. */
  portefeuille: Portefeuille;
  /** Projets effectivement retenus, pour le témoin. */
  portefeuilleActif: Portefeuille;
  similarites: Similarites;
  aujourdhui: DateIso;
  poidsBruts: Poids;
  surPoids: (poids: Poids) => void;
  etatsInclus: ReadonlySet<EtatTemporel>;
  surEtats: (etats: ReadonlySet<EtatTemporel>) => void;
  placement: Placement;
  surPlacement: (p: Placement) => void;
  surReinitialisation: () => void;
}) {
  const dimensions = () => props.similarites.dimensionsUtilisees;
  const temoin = createMemo(() =>
    controlePlansAction(props.portefeuilleActif.projets, props.similarites, PAIRES_TEMOIN),
  );
  const etats = createMemo(() => etatsPresents(props.portefeuille, props.aujourdhui));
  const presetActif = (poids: Poids) =>
    dimensions().every((d) => Math.abs(props.poidsBruts[d] - poids[d]) < 0.005);

  return (
    <div class="vue-texte">
      <ReglageApparence />

      <Show when={etats().length > 1}>
        <section class="carte carte-parametre" aria-labelledby="reglage-etats">
          <h2 id="reglage-etats">Projets inclus</h2>
          <p class="explication discret">Décocher retire les projets de toutes les vues.</p>
          <div class="cases">
            <For each={etats()}>
              {(e) => (
                <label class="case">
                  <input
                    type="checkbox"
                    checked={props.etatsInclus.has(e.etat)}
                    // Au moins un état reste coché : un portefeuille vide n'a rien à montrer.
                    disabled={props.etatsInclus.has(e.etat) && props.etatsInclus.size === 1}
                    onChange={(ev) => {
                      const suivants = new Set(props.etatsInclus);
                      if (ev.currentTarget.checked) suivants.add(e.etat);
                      else suivants.delete(e.etat);
                      props.surEtats(suivants);
                    }}
                  />
                  <span>
                    {LIBELLES_ETAT_FILTRE[e.etat]} ({e.n})
                  </span>
                </label>
              )}
            </For>
          </div>
        </section>
      </Show>

      <section class="carte carte-parametre" aria-labelledby="reglage-poids">
        <h2 id="reglage-poids">Pondération de la ressemblance</h2>
        <p class="explication discret">Poids non validés. Le graphe garde sa densité.</p>
        <div class="parametre-corps">
          <div class="calibrage-curseurs">
            <For each={dimensions()}>
              {(d) => (
                <div class="calibrage-ligne">
                  <label class="intitule" for={`poids-${d}`}>
                    {LIBELLES_DIMENSION[d]}
                  </label>
                  <input
                    id={`poids-${d}`}
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={Math.round(props.poidsBruts[d] * 100)}
                    style={{ "--rempli": `${Math.round(props.poidsBruts[d] * 100)}%` }}
                    onInput={(e) =>
                      props.surPoids({
                        ...props.poidsBruts,
                        [d]: Number(e.currentTarget.value) / 100,
                      })
                    }
                  />
                  <output class="lecture" for={`poids-${d}`}>
                    {formaterScore(props.similarites.poids[d] ?? 0)}
                  </output>
                </div>
              )}
            </For>
          </div>
          <div class="parametre-cote">
            <div class="calibrage-presets">
              <span class="intitule">Essayer</span>
              <div class="boutons">
                <For each={PONDERATIONS}>
                  {(p) => (
                    <button
                      type="button"
                      class="bouton"
                      classList={{ actif: presetActif(p.poids) }}
                      aria-pressed={presetActif(p.poids)}
                      onClick={() => props.surPoids({ ...p.poids })}
                    >
                      {p.nom}
                    </button>
                  )}
                </For>
              </div>
            </div>
            <div class="calibrage-temoin" aria-live="polite">
              <span class="intitule">Contrôle : plan d'action (hors calcul)</span>
              <p class="temoin-valeur">
                <strong>
                  {temoin().memes} sur {temoin().total}
                </strong>{" "}
                des paires les plus proches partagent leur plan d'action.
              </p>
              <p class="explication discret">
                Au hasard :{" "}
                {(temoin().hasard * 100).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} %.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section class="carte carte-parametre" aria-labelledby="reglage-placement">
        <h2 id="reglage-placement">Placement du réseau</h2>
        <p class="explication discret">
          Ce qui rapproche les projets à l'écran. Les liens tracés ne changent pas.
        </p>
        <div class="choix-placement" role="radiogroup" aria-labelledby="reglage-placement">
          <For each={PLACEMENTS}>
            {(p) => (
              <label class="case choix-placement-option">
                <input
                  type="radio"
                  name="placement"
                  checked={props.placement === p.id}
                  onChange={() => props.surPlacement(p.id)}
                />
                <span>
                  <strong>{p.libelle}</strong>
                  <span class="explication discret">{p.detail}</span>
                </span>
              </label>
            )}
          </For>
        </div>
      </section>

      <p class="parametre-pied">
        <button type="button" class="bouton" onClick={() => props.surReinitialisation()}>
          Tout réinitialiser
        </button>
      </p>
    </div>
  );
}
